import {
    BotRequest,
    BotState,
    BotResponse,
    ResponseElement,
    BotErrorResponse,
    ForLoopState
} from './context'
import { Message } from './message'
import { Conversation } from '../model/conversation'
import { Question } from '../model/question'
import { Response } from '../model/response'
import { Pause } from '../model/pause'
import { Goto } from '../model/goto'
import { Renderer, RendererType } from '../render/types'

class Engine {
    conversation: Conversation

    constructor(conversation: Conversation) {
        this.conversation = conversation
    }

    initiate(id: string, context?: object): BotResponse | null {
        let segment = this.conversation.getSegment(id)

        if (segment === null) {
            return null // FIXME: return BotErroResponse
        }

        let state = new BotState(segment)
        if (context !== undefined) {
            state.context = context || {}
        }

        let request = new BotRequest(state)

        return this.feed(request)
    }

    contextForState(state: BotState) {
        let context = state.context
        if (state.forLoopState !== null) {
            context = state.forLoopState.newContextWithForloop(context)
        }
        return context
    }

    generateNextElements(request: BotRequest): BotResponse {
        let botState = request.state.newStateWithContext(request.state.context)
        let segment = request.state.segment

        let nextElements: ResponseElement[] = []
        let nextIndex = botState.activeIndex
        do {
            let element = segment.elements[nextIndex]
            nextIndex++

            if (!element.shouldShow(this.contextForState(botState))) {
                continue
            }

            if (element instanceof Goto) {
                let nextSegment = this.conversation.getSegment(element.goto)
                if (nextSegment !== null) {
                    let nextItemFrom = element.withNextItemFrom
                    if (nextItemFrom !== null) {
                        this.adjustForLoopState(botState, nextItemFrom)
                    }
                    nextIndex = 0
                    segment = nextSegment
                    continue
                } else {
                    break
                }
            }

            nextElements.push(
                new ResponseElement(segment, nextIndex - 1, element, this.contextForState(botState))
            )

            if (element.shouldStop(botState.context)) {
                break
            }
        } while (nextIndex < segment.elements.length)

        let newState = new BotState(segment)
        newState.activeIndex = nextIndex
        newState.context = botState.context
        newState.forLoopState = botState.forLoopState

        let response = new BotResponse(newState, nextElements)

        return response
    }

    adjustForLoopState(state: BotState, nextItemVar: string) {
        let forLoopState: ForLoopState | null = null

        if (state.forLoopState !== null) {
            if (nextItemVar != state.forLoopState.varName) {
                // conversation passed in a new next-item-from value, cancel the old loop and start a new one
                forLoopState = new ForLoopState(nextItemVar)
            } else {
                forLoopState = new ForLoopState(state.forLoopState.varName)
                forLoopState.index += state.forLoopState.index + 1
            }
        } else {
            forLoopState = new ForLoopState(nextItemVar)
        }
        state.forLoopState = forLoopState
    }

    handleQuestionInput(request: BotRequest, question: Question) {
        let requestInput = request.input as { presentation: string; id: any }
        let responseElement = new Response(requestInput.presentation || `${requestInput.id}`)
        let destination = question.gotoForOptionId(requestInput.id!)

        if (destination === null) {
            return new BotErrorResponse(request, 'Invalid destination')
        }

        let segment = this.conversation.getSegment(destination!)

        if (segment === null) {
            return new BotErrorResponse(request, 'No segment matching id')
        }

        let state = new BotState(segment)
        state.context = { ...request.state.context }
        state.forLoopState = request.state.forLoopState

        let option = question.optionForOptionId(requestInput.id!)
        if (option !== null) {
            let nextItemFrom = option['with-next-item-from']
            if (nextItemFrom !== undefined) {
                this.adjustForLoopState(state, nextItemFrom)
            }
        }

        let nextRequest = new BotRequest(state)

        let response = this.feed(nextRequest)

        if (response === null) {
            return new BotErrorResponse(request, 'Invalid destination')
        }

        response.request = request
        response.elements.unshift(
            new ResponseElement(
                request.state.segment,
                -1,
                responseElement,
                this.contextForState(request.state)
            )
        )

        return response
    }

    handlePauseInput(request: BotRequest, pause: Pause) {
        let requestInput = request.input as { presentation: string }
        let responseElement = new Response(requestInput.presentation)

        let response = this.generateNextElements(request)
        response.elements.unshift(
            new ResponseElement(
                request.state.segment,
                -1,
                responseElement,
                this.contextForState(request.state)
            )
        )

        return response
    }

    feed(request: BotRequest): BotResponse | BotErrorResponse | null {
        let botState = request.state
        let segment = request.state.segment

        if (Object.keys(request.input).length > 0) {
            // process goto
            let previousElement = segment.elements[botState.activeIndex - 1]
            if (previousElement instanceof Question) {
                return this.handleQuestionInput(request, previousElement)
            } else if (previousElement instanceof Pause) {
                return this.handlePauseInput(request, previousElement)
            }

            return new BotErrorResponse(request, 'No matching request type')
        } else {
            // render some more
            return this.generateNextElements(request)
        }
    }

    render(response: BotResponse): Message[] {
        if (response.elements.length === 0) {
            return []
        }

        let currentMood = null

        let messages = []

        for (const responseElement of response.elements) {
            let elementType = responseElement.element.type
            let cls: RendererType = Renderer.rendererClasses[elementType]
            let renderer = new cls()
            let context = responseElement.renderContext
            let responseMood = responseElement.segment.avatarMood
            if (responseMood !== currentMood) {
                messages.push(new Message('mood', { state: responseMood }))
                currentMood = responseMood
            }
            messages.push(renderer.render(responseElement.element, context))
        }

        return messages
    }
}

export { Engine }
